import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ArrowLeft, Crown, Sparkles, CreditCard, TrendingUp, TrendingDown, Leaf, Loader2, User, BellRing } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');

const CycleAlertsToggle = ({ user }) => {
  const [enabled, setEnabled] = useState(user?.cycle_alerts !== false);
  const [saving, setSaving] = useState(false);

  const toggle = async (val) => {
    setEnabled(val);
    setSaving(true);
    try {
      await axios.patch(`${API}/user/preferences`, { cycle_alerts: val }, { withCredentials: true });
      toast.success(val ? 'Predictive cycle alerts on — we\'ll warn you before your luteal breakout window.' : 'Cycle alerts turned off.');
    } catch (e) {
      setEnabled(!val);
      toast.error('Could not update preference');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="w-full flex items-center justify-between bg-[#FFF2EC] rounded-xl px-4 py-3 mt-1" data-testid="cycle-alerts-row">
      <div className="flex items-center gap-2.5">
        <BellRing className="w-4 h-4 text-[#A388EE] flex-shrink-0" />
        <div>
          <p className="text-xs font-semibold text-[#1A1514]">Predictive cycle alerts</p>
          <p className="text-[10px] text-[#8C7A76]">Push warning ~3 days before your luteal breakout window</p>
        </div>
      </div>
      <Switch checked={enabled} onCheckedChange={toggle} disabled={saving} data-testid="cycle-alerts-switch" />
    </div>
  );
};

const ProfileCard = ({ user }) => (
  <Card className="card-elevated border-0 h-full" data-testid="account-profile-card">
    <CardContent className="p-6 flex flex-col items-start gap-4">
      <div className="w-16 h-16 rounded-full bg-[#FF6B4A] text-white flex items-center justify-center text-2xl font-bold overflow-hidden">
        {user?.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" /> : (user?.name?.[0]?.toUpperCase() || <User className="w-7 h-7" />)}
      </div>
      <div>
        <p className="overline text-[#A388EE] text-xs mb-1">Profile</p>
        <h2 className="font-heading text-2xl font-bold text-[#1A1514]">{user?.name}</h2>
        <p className="text-sm text-[#8C7A76]" data-testid="account-email">{user?.email}</p>
      </div>
      <div className="flex flex-wrap gap-2 text-xs">
        <span className="bg-[#F3E6E0] text-[#1A1514] px-3 py-1 rounded-full font-semibold">
          {user?.auth_provider === 'password' ? 'Email account' : 'Google account'}
        </span>
        <span className="bg-[#F3E6E0] text-[#1A1514] px-3 py-1 rounded-full font-semibold">
          Member since {fmtDate(user?.created_at)}
        </span>
        {user?.level != null && (
          <span className="bg-[#A388EE]/15 text-[#A388EE] px-3 py-1 rounded-full font-semibold">Level {user.level} · {user.xp || 0} XP</span>
        )}
      </div>
      {user?.track_cycle !== false && <CycleAlertsToggle user={user} />}
    </CardContent>
  </Card>
);

const SkinSummaryCard = ({ selfies, history }) => {
  const latest = selfies.find((s) => s.analysis?.overall_score != null);
  const prev = selfies.filter((s) => s.analysis?.overall_score != null)[1];
  const score = latest?.analysis?.overall_score;
  const delta = prev ? score - prev.analysis.overall_score : null;
  const bars = [...history].reverse();
  return (
    <Card className="card-elevated border-0 h-full lg:col-span-2" data-testid="account-skin-summary-card">
      <CardHeader className="pb-2">
        <p className="overline text-[#A388EE] text-xs">Skin Summary</p>
        <CardTitle className="text-[#1A1514] font-heading text-xl">Your glow at a glance</CardTitle>
      </CardHeader>
      <CardContent className="p-6 pt-2">
        <div className="flex flex-col sm:flex-row gap-8">
          <div className="flex items-center gap-4">
            <div className="relative w-24 h-24 flex-shrink-0">
              <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                <circle cx="50" cy="50" r="42" fill="none" stroke="#F3E6E0" strokeWidth="10" />
                <circle cx="50" cy="50" r="42" fill="none" stroke="#A388EE" strokeWidth="10" strokeLinecap="round"
                  strokeDasharray={`${(score || 0) * 2.64} 264`} />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-2xl font-bold text-[#1A1514]" data-testid="account-skin-score">{score ?? '—'}</span>
                <span className="text-[10px] text-[#8C7A76]">/ 100</span>
              </div>
            </div>
            <div>
              <p className="text-sm font-semibold text-[#1A1514]">Latest AI scan</p>
              <p className="text-xs text-[#8C7A76] mb-1">{latest ? fmtDate(latest.created_at) : 'No scan yet'}</p>
              {delta != null && (
                <p className={`flex items-center gap-1 text-sm font-bold ${delta >= 0 ? 'text-[#4ADE80]' : 'text-[#FF3B30]'}`} data-testid="account-skin-delta">
                  {delta >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                  {delta >= 0 ? '+' : ''}{delta} pts since last scan
                </p>
              )}
            </div>
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-[#1A1514] mb-2">Detox Score — last {bars.length || 0} days</p>
            {bars.length > 0 ? (
              <div className="flex items-end gap-1.5 h-20" data-testid="account-detox-trend">
                {bars.map((d) => (
                  <div key={d.date} className="flex-1 flex flex-col items-center gap-1" title={`${d.date}: ${d.total_score}`}>
                    <div className="w-full rounded-t-md bg-[#FF6B4A]" style={{ height: `${Math.max(6, d.total_score)}%`, opacity: 0.5 + d.total_score / 200 }} />
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-[#8C7A76]">Log daily to build your trend.</p>
            )}
            <Link to="/dashboard?tab=progress" className="inline-block mt-3 text-sm font-semibold text-[#FF6B4A] hover:underline" data-testid="account-view-progress-link">
              View full skin progress →
            </Link>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const SubscriptionCard = ({ status, onCancelled }) => {
  const navigate = useNavigate();
  const [cancelling, setCancelling] = useState(false);
  const isPremium = status?.subscription === 'premium';
  const isTrial = status?.plan_id === 'free_trial';
  const cancelled = status?.cancel_at_period_end;

  const cancel = async () => {
    if (!window.confirm('Cancel your Premium subscription? You keep Premium until the end of your billing period.')) return;
    setCancelling(true);
    try {
      const { data } = await axios.post(`${API}/subscription/cancel`, {}, { withCredentials: true });
      toast.success(data.message);
      onCancelled();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not cancel subscription');
    } finally {
      setCancelling(false);
    }
  };

  return (
    <Card className="card-elevated border-0 h-full" id="billing" data-testid="account-subscription-card">
      <CardHeader className="pb-2">
        <p className="overline text-[#A388EE] text-xs">Subscription</p>
        <CardTitle className="text-[#1A1514] font-heading text-xl flex items-center gap-2">
          {isPremium ? <><Crown className="w-5 h-5 text-[#FF6B4A]" /> Premium</> : <><Sparkles className="w-5 h-5 text-[#8C7A76]" /> Free plan</>}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6 pt-2 space-y-4">
        {isPremium ? (
          <>
            <div className="text-sm text-[#8C7A76] space-y-1">
              <p><span className="font-semibold text-[#1A1514]">{status.plan_name || (isTrial ? '7-day Free Trial' : 'Premium')}</span>{status.amount ? ` — $${status.amount}/${status.interval || 'mo'}` : ''}</p>
              {cancelled ? (
                <p className="text-[#FF3B30] font-semibold" data-testid="subscription-cancelled-note">Cancelled — Premium active until {fmtDate(status.premium_until)}</p>
              ) : (
                <p>{isTrial ? `Trial ends ${fmtDate(status.premium_until)}` : `Renews ${fmtDate(status.renewal_date)}`}</p>
              )}
            </div>
            {!cancelled && !isTrial && (
              <Button variant="outline" onClick={cancel} disabled={cancelling}
                className="rounded-full border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30]/5 font-semibold"
                data-testid="cancel-subscription-button">
                {cancelling ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Cancel subscription'}
              </Button>
            )}
            {isTrial && (
              <Button onClick={() => navigate('/dashboard?tab=subscription')}
                className="rounded-full bg-[#FF6B4A] hover:bg-[#E8552F] text-white font-semibold" data-testid="upgrade-from-trial-button">
                Upgrade to keep Premium
              </Button>
            )}
          </>
        ) : (
          <>
            <p className="text-sm text-[#8C7A76]">Unlock the 30-point clinical AI scan, unlimited selfies, and matched product picks.</p>
            <Button onClick={() => navigate('/dashboard?tab=subscription')}
              className="rounded-full bg-[#FF6B4A] hover:bg-[#E8552F] text-white font-semibold" data-testid="upgrade-subscription-button">
              <Crown className="w-4 h-4 mr-2" /> Upgrade to Premium
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
};

const statusStyle = (s) =>
  s === 'paid' || s === 'complete' ? 'bg-[#4ADE80]/15 text-[#15803D]'
    : s === 'initiated' || s === 'pending' ? 'bg-[#F3E6E0] text-[#8C7A76]'
    : 'bg-[#FF3B30]/10 text-[#FF3B30]';

const PaymentHistoryCard = ({ payments }) => (
  <Card className="card-elevated border-0 h-full lg:col-span-2" data-testid="account-payment-history-card">
    <CardHeader className="pb-2">
      <p className="overline text-[#A388EE] text-xs">Billing</p>
      <CardTitle className="text-[#1A1514] font-heading text-xl flex items-center gap-2">
        <CreditCard className="w-5 h-5 text-[#FF6B4A]" /> Payment history
      </CardTitle>
    </CardHeader>
    <CardContent className="p-6 pt-2">
      {payments.length === 0 ? (
        <p className="text-sm text-[#8C7A76] py-4">No payments yet. Your Stripe transactions will appear here.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-[#8C7A76]">Date</TableHead>
              <TableHead className="text-[#8C7A76]">Item</TableHead>
              <TableHead className="text-[#8C7A76]">Amount</TableHead>
              <TableHead className="text-[#8C7A76]">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {payments.map((p) => (
              <TableRow key={p.transaction_id} data-testid="payment-history-row">
                <TableCell className="text-[#1A1514]">{fmtDate(p.created_at)}</TableCell>
                <TableCell className="text-[#1A1514] capitalize">{(p.plan_id || p.metadata?.plan_id || 'One-time scan').replace(/_/g, ' ')}</TableCell>
                <TableCell className="font-semibold text-[#1A1514]">${Number(p.amount).toFixed(2)} {String(p.currency || 'usd').toUpperCase()}</TableCell>
                <TableCell>
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${statusStyle(p.payment_status)}`}>{p.payment_status}</span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </CardContent>
  </Card>
);

export default function Account() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [payments, setPayments] = useState([]);
  const [selfies, setSelfies] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [s, p, sf, h] = await Promise.all([
        axios.get(`${API}/subscription/status`, { withCredentials: true }),
        axios.get(`${API}/payments/history`, { withCredentials: true }),
        axios.get(`${API}/selfies?limit=5`, { withCredentials: true }),
        axios.get(`${API}/detox-score/history?days=14`, { withCredentials: true }),
      ]);
      setStatus(s.data); setPayments(p.data); setSelfies(sf.data); setHistory(h.data);
    } catch (e) {
      toast.error('Could not load account data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    if (window.location.hash === '#billing') {
      setTimeout(() => document.getElementById('billing')?.scrollIntoView({ behavior: 'smooth' }), 400);
    }
  }, []);

  return (
    <div className="min-h-screen bg-[#FFF2EC]" data-testid="account-page">
      <header className="glass-header sticky top-0 z-50 px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center gap-4">
          <Link to="/dashboard" className="flex items-center gap-2 text-[#8C7A76] hover:text-[#FF6B4A] transition-colors" data-testid="account-back-link">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="flex items-center gap-2">
            <Leaf className="w-7 h-7 text-[#FF6B4A]" />
            <span className="font-heading text-lg font-bold text-[#1A1514]">My Account</span>
          </div>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-6 py-10">
        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-[#FF6B4A]" /></div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <ProfileCard user={user} />
            <SkinSummaryCard selfies={selfies} history={history} />
            <SubscriptionCard status={status} onCancelled={load} />
            <PaymentHistoryCard payments={payments} />
          </div>
        )}
      </main>
    </div>
  );
}
